/**
 * Deverrouillage du coffre par passkey (WebAuthn PRF) — item R12,
 * `docs/ARCHITECTURE.md` §1 et §4.
 *
 * CE QUE C'EST : un RACCOURCI local. La sortie PRF de la passkey scelle une
 * COPIE de la `vaultKey` sur cet appareil ; presenter la passkey rouvre le
 * coffre sans taper le mot de passe maitre, ni derivation Argon2id.
 *
 * CE QUE CE N'EST PAS : une nouvelle racine. Le mot de passe maitre reste la
 * seule racine NON DELEGABLE (il scelle la `vaultKey` de son cote, voie
 * inchangee) :
 *  - revoquer une passkey = retirer sa fiche locale. Le coffre n'est pas
 *    touche, aucune entree n'est rechiffree, aucune revision n'est incrementee ;
 *  - perdre la passkey ne perd pas le coffre : le mot de passe maitre ouvre
 *    toujours, la cle de recuperation reste valable ;
 *  - le blob ne quitte JAMAIS l'appareil -- hors de `AccountEnvelope`, donc
 *    jamais synchronise, jamais embarque dans un export (cf. `types.ts`). Une
 *    passkey synchronisee entre appareils (iCloud, Google) n'ouvre donc que
 *    l'appareil ou elle a ete enrolee, tant qu'elle n'y est pas enrolee aussi.
 *
 * Ordre volontaire, calque sur `master-password-change.ts` : la ceremonie
 * d'abord, la persistance SEULEMENT si elle a reussi -- une passkey refusee ou
 * annulee laisse l'appareil exactement dans son etat d'avant.
 */
import {
  DecryptionFailedError,
  InvalidParameterError,
  adoptPasskeyPrfOutput,
  base64ToBytes,
  bytesToBase64,
  generatePasskeyPrfSalt,
  randomBytes,
  unprotectVaultKeyWithPasskey,
  type SecretKey,
} from '@klavox/crypto-core';
import type { VaultRepository } from './repository.js';
import type { AccountEnvelope, PasskeyUnlockRecord } from './types.js';
import type { VaultStore } from './vault-store.js';
import {
  PasskeyPrfUnsupportedError,
  WebAuthnUnavailableError,
  createPasskeyWithPrf,
  evaluatePasskeyPrf,
  isWebAuthnSupported,
} from './webauthn-browser.js';

/** Nom affiche par le selecteur de passkey de l'OS. */
const RP_NAME = 'Klavox';
/** Meme plancher que le libelle d'une cle de securite (2FA), pour la meme raison : c'est un nom. */
export const MAX_PASSKEY_LABEL_LENGTH = 64;
/** Au-dela, ce n'est plus un raccourci mais une collection : autant de copies scellees de la `vaultKey`. */
export const MAX_PASSKEY_UNLOCKS = 5;

export class NoPasskeyUnlockError extends Error {
  constructor() {
    super('aucune passkey de deverrouillage enregistree sur cet appareil');
    this.name = 'NoPasskeyUnlockError';
  }
}

export class TooManyPasskeyUnlocksError extends Error {
  constructor() {
    super(`${MAX_PASSKEY_UNLOCKS} passkeys de deverrouillage au maximum sur un appareil`);
    this.name = 'TooManyPasskeyUnlocksError';
  }
}

export interface EnrollPasskeyUnlockInput {
  readonly repository: VaultRepository;
  /** Coffre DEVERROUILLE : on ne peut sceller que ce qui est deja ouvert. */
  readonly vaultStore: VaultStore;
  /** E-mail du compte : libelle du selecteur de l'OS, jamais un secret. */
  readonly email: string;
  readonly label: string;
  /** Point d'injection des tests ; en production, `navigator.credentials`. */
  readonly container?: CredentialsContainer;
}

/**
 * Enrole une passkey comme raccourci de deverrouillage de CET appareil.
 * La sortie PRF est effacee avant de rendre la main : seul le blob scelle reste.
 */
export async function enrollPasskeyUnlock(input: EnrollPasskeyUnlockInput): Promise<PasskeyUnlockRecord> {
  if (!isWebAuthnSupported()) throw new WebAuthnUnavailableError('Ce navigateur ne prend pas en charge les passkeys (WebAuthn).');
  const existing = await input.repository.listPasskeyUnlocks();
  if (existing.length >= MAX_PASSKEY_UNLOCKS) throw new TooManyPasskeyUnlocksError();

  const salt = generatePasskeyPrfSalt();
  const created = await createPasskeyWithPrf(
    {
      rpName: RP_NAME,
      // Identifiant de compte cote authentificateur : aleatoire et local, il ne
      // sert qu'a distinguer les passkeys. L'e-mail reste le libelle affiche
      // (coffre cree sans e-mail : un libelle neutre, WebAuthn en exige un).
      userId: randomBytes(16),
      userName: input.email.trim() === '' ? 'Coffre Klavox' : input.email,
      salt,
      excludeCredentialIds: existing.map((record) => record.credentialId),
    },
    input.container,
  );

  const prfOutput = adoptPasskeyPrfOutput(created.prfOutput);
  let protectedSymmetricKey: string;
  try {
    protectedSymmetricKey = input.vaultStore.sealForPasskey(prfOutput);
  } finally {
    prfOutput.destroy();
  }

  const record: PasskeyUnlockRecord = {
    credentialId: created.credentialId,
    label: input.label.trim().slice(0, MAX_PASSKEY_LABEL_LENGTH) || 'Passkey de cet appareil',
    createdAt: new Date().toISOString(),
    prfSaltBase64: bytesToBase64(salt),
    protectedSymmetricKey,
  };
  // Un re-enrolement de la MEME passkey remplace sa fiche au lieu de la doubler
  // (sel et blob neufs) : l'ancienne copie scellee n'a plus a exister.
  await input.repository.savePasskeyUnlocks([
    ...existing.filter((other) => other.credentialId !== record.credentialId),
    record,
  ]);
  return record;
}

export interface UnlockWithPasskeyInput {
  readonly repository: VaultRepository;
  readonly container?: CredentialsContainer;
}

export interface UnlockedByPasskey {
  readonly envelope: AccountEnvelope;
  /** `vaultKey` deverrouillee : l'appelant la confie a un `VaultStore` (qui la detruit au verrouillage). */
  readonly vaultKey: SecretKey;
  /** La passkey effectivement presentee. */
  readonly record: PasskeyUnlockRecord;
}

/**
 * Deverrouille le coffre local avec l'une des passkeys enrolees.
 *
 * @throws {NoPasskeyUnlockError} aucune passkey enrolee ici
 * @throws {PasskeyPrfUnsupportedError} authentificateur sans PRF exploitable
 * @throws {WebAuthnUnavailableError} ceremonie annulee, expiree ou impossible
 * @throws {DecryptionFailedError} blob altere, ou passkey qui n'ouvre pas ce
 *   coffre (le mot de passe maitre, lui, ouvre toujours)
 */
export async function unlockVaultWithPasskey(input: UnlockWithPasskeyInput): Promise<UnlockedByPasskey> {
  const envelope = await input.repository.loadAccountEnvelope();
  if (envelope === null) throw new NoPasskeyUnlockError();
  const records = await input.repository.listPasskeyUnlocks();
  if (records.length === 0) throw new NoPasskeyUnlockError();

  const salts: Record<string, Uint8Array> = {};
  for (const record of records) salts[record.credentialId] = base64ToBytes(record.prfSaltBase64);
  const evaluated = await evaluatePasskeyPrf({ credentialIds: records.map((r) => r.credentialId), salts }, input.container);

  // L'authentificateur choisit la passkey : c'est SA reponse qui designe la fiche.
  const record = records.find((candidate) => candidate.credentialId === evaluated.credentialId);
  const prfOutput = adoptPasskeyPrfOutput(evaluated.prfOutput);
  try {
    if (record === undefined) throw new PasskeyPrfUnsupportedError('Cette passkey n’est pas enregistree pour deverrouiller ce coffre.');
    const vaultKey = unprotectVaultKeyWithPasskey({ protectedKey: record.protectedSymmetricKey, prfOutput });
    return { envelope, vaultKey, record };
  } finally {
    prfOutput.destroy();
  }
}

export interface RevokePasskeyUnlockInput {
  readonly repository: VaultRepository;
  readonly credentialId: string;
}

/**
 * Revoque une passkey de deverrouillage : sa fiche disparait de cet appareil,
 * et avec elle la copie scellee de la `vaultKey`. Le coffre n'est pas touche.
 * La passkey elle-meme reste sur l'authentificateur (nous ne pouvons pas l'en
 * retirer) : elle n'ouvre simplement plus rien ici.
 */
export async function revokePasskeyUnlock(input: RevokePasskeyUnlockInput): Promise<PasskeyUnlockRecord[]> {
  const records = await input.repository.listPasskeyUnlocks();
  const remaining = records.filter((record) => record.credentialId !== input.credentialId);
  if (remaining.length !== records.length) await input.repository.savePasskeyUnlocks(remaining);
  return remaining;
}

/** Message utilisateur sans detail technique : dit toujours si le coffre reste ouvrable autrement. */
export function describePasskeyUnlockError(cause: unknown): string {
  if (cause instanceof NoPasskeyUnlockError) {
    return 'Aucune passkey enregistree sur cet appareil : deverrouillez avec votre mot de passe maitre.';
  }
  if (cause instanceof TooManyPasskeyUnlocksError) return cause.message;
  if (cause instanceof PasskeyPrfUnsupportedError) return cause.message;
  if (cause instanceof WebAuthnUnavailableError) return cause.message;
  if (cause instanceof DecryptionFailedError) {
    return 'Cette passkey n’ouvre pas ce coffre : deverrouillez avec votre mot de passe maitre.';
  }
  if (cause instanceof InvalidParameterError) return 'Reponse de la passkey inexploitable : rien n’a ete modifie.';
  return cause instanceof Error ? `Echec : ${cause.message}` : 'Echec du deverrouillage par passkey';
}
